import { View } from 'backbone.marionette';
import _ from 'lodash';
import Backbone from 'backbone';
import Ladda from 'ladda';

import showErrorMessage from '@/util/showErrorMessage';
import OrderFileUploadTmpl from '@/fileupload/OrderFileUploadTmpl.hbs';
import AttachmentsCollectionView from '@/views/AttachmentsCollectionView';
import FileUploadModel from '@/fileupload/FileUploadModel';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';

import tmplAdHocAttachmentsView from './AdHocAttachmentsView.hbs';

const AD_HOC_DOCUMENT_TYPE_ID = 40;

const AdHocAttachmentsView = View.extend({
  template: tmplAdHocAttachmentsView,

  className: 'ad-hoc-attachments-view',

  ui: {
    attachmentsUploads: '.ad-hoc-attachments-uploads',
    uploadButton: '.btn-upload'
  },

  regions: {
    attachmentsUploads: '@ui.attachmentsUploads'
  },

  events: {
    'click @ui.uploadButton': 'onClickUploadButton',
    'change #file-upload-input': 'onFileSelect'
  },

  childEvents: {
    'file:deleted': 'onFileDelete'
  },

  childViewEvents: {
    'file:deleted': 'onFileDelete'
  },

  collectionEvents: {
    'file:deleted': 'onFileDelete'
  },

  initialize() {
    this.model.set('submittedOn', new Date().toISOString().substring(0, 10));
    const attachments = this.model.get('attachments');
    const adHocAttachmentMapping = _.map(attachments, (attachment) => {
      return {
        label: { isHidden: true },
        isReadOnly: !this.model.get('editable'),
        attachment: {
          isReadOnly: !this.model.get('editable'),
          attachmentName: attachment?.fileName,
          attachmentId: attachment?.id,
          fileUploadTmpl: OrderFileUploadTmpl,
          pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
          metadata: {
            orderId: this.model.get('orderId'),
            attachmentId: attachment?.id,
            fileName: attachment?.fileName,
            documentTypeId: AD_HOC_DOCUMENT_TYPE_ID
          }
        }
      };
    });
    this.model.set('adHocAttachmentMapping', adHocAttachmentMapping);
  },

  onRender() {
    this.collection = new Backbone.Collection(this.model.get('adHocAttachmentMapping'));
    this.attachmentsCollectionView = new AttachmentsCollectionView({
      collection: this.collection
    });
    this.showChildView('attachmentsUploads', this.attachmentsCollectionView);
    this.styleAttachments();
  },

  onClickUploadButton(e) {
    e.preventDefault();
    this.$('#file-upload-input').trigger('click');
  },

  onFileSelect(e) {
    e.preventDefault();
    const files = e.target.files || e.originalEvent.dataTransfer.files;
    const file = files[0];
    this.uploadFile(file);
    this.trigger('upload:complete');
  },

  async uploadFile(file) {
    if (!ValidFileTypeUtil(file)) {
      return;
    }

    const orderId = this.model.get('orderId');
    const fileUploadModel = new FileUploadModel({
      file1: file,
      attachmentName: file.name,
      fileName: file.name,
      showError: this.showUploadError,
      isReadOnly: !this.model.get('editable'),
      metadata: {
        orderId,
        documentTypeId: AD_HOC_DOCUMENT_TYPE_ID
      },
      pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
      tmplMethodXML: OrderFileUploadTmpl
    });

    const spinner = Ladda.create(this.ui.uploadButton[0]);
    spinner.start();

    try {
      await fileUploadModel.upload();

      const uploadedAttachment = {
        label: { isHidden: true },
        isReadOnly: !this.model.get('editable'),
        attachment: {
          isReadOnly: !this.model.get('editable'),
          attachmentName: fileUploadModel.get('fileName'),
          attachmentId: fileUploadModel.get('attachmentId'),
          fileUploadTmpl: fileUploadModel.get('tmplMethodXML'),
          pathToFileId: fileUploadModel.get('pathToFileId'),
          validator: ValidFileTypeUtil,
          metadata: fileUploadModel.get('metadata')
        }
      };
      this.attachmentsCollectionView.collection.add(uploadedAttachment);
    } catch (err) {
      if (err && err.responseText.includes('already exists')) {
        this.showDuplicateFileNameError();
      }
    } finally {
      spinner.stop();
      this.styleAttachments();
    }
  },

  getAttachmentItemViewData() {},

  styleAttachments() {
    if (this.collection?.models?.length > 0) {
      this.ui.attachmentsUploads.removeClass('offset-md-3');
      this.ui.attachmentsUploads.addClass('offset-md-6');
    } else {
      this.ui.attachmentsUploads.removeClass('offset-md-6');
      this.ui.attachmentsUploads.addClass('offset-md-3');
    }
  },

  showUploadError() {
    showErrorMessage({
      errorCode: 'fileUploadError',
      errorTitle: 'File Upload Error',
      errorMessage: 'Error Uploading File.'
    });
  },

  onFileDelete(modelToDelete) {
    this.attachmentsCollectionView.collection.remove(modelToDelete);
    this.styleAttachments();
    this.trigger('file:deleted');
  },

  showDuplicateFileNameError() {
    showErrorMessage({
      errorCode: 'documentExists',
      errorTitle: 'Duplicate File Name',
      errorMessage: 'All uploaded documents must have a unique file name. Please update the file name and try again.'
    });
  }
});

export default AdHocAttachmentsView;
